import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { X, CreditCard, DollarSign } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID;
const PAYPAL_EMAIL = 'tanyakhaleel@gmail.com';

// Live exchange rate cache
let cachedRate = 84;
let rateFetchedAt = 0;

const fetchLiveRate = async () => {
  if (Date.now() - rateFetchedAt < 600000) return cachedRate; // cache 10 min
  try {
    const { data } = await axios.get(`${API_URL}/api/exchange-rate`);
    if (data.rate) { cachedRate = data.rate; rateFetchedAt = Date.now(); }
  } catch (e) { if (process.env.NODE_ENV !== 'production') console.warn('Exchange rate fetch failed, using cached:', cachedRate); }
  return cachedRate;
};

// Currency toggle
export const CurrencyToggle = ({ currency, setCurrency }) => (
  <div className="inline-flex items-center rounded-full p-1" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
    {['USD', 'INR'].map(c => (
      <button
        key={c}
        onClick={() => setCurrency(c)}
        className={`px-3 py-1 rounded-full text-xs font-heading font-bold transition-all ${
          currency === c
            ? 'bg-[#FF00FF] text-white shadow-[0_0_8px_rgba(255,0,255,0.4)]'
            : 'text-white/50 hover:text-white/80'
        }`}
        data-testid={`currency-toggle-${c}`}
      >
        {c === 'USD' ? '$' : '₹'} {c}
      </button>
    ))}
  </div>
);

export const convertPrice = (amount, fromCurrency, toCurrency) => {
  if (fromCurrency === toCurrency) return amount;
  if (fromCurrency === 'USD' && toCurrency === 'INR') return Math.round(amount * cachedRate);
  if (fromCurrency === 'INR' && toCurrency === 'USD') return Math.round((amount / cachedRate) * 100) / 100;
  return amount;
};

export const formatPrice = (amount, currency) =>
  currency === 'USD' ? `$${amount.toLocaleString()}` : `₹${amount.toLocaleString()}`;

// Checkout Modal — fixed touch handling
export const CheckoutModal = ({ isOpen, onClose, itemType, itemName, baseAmount, baseCurrency, onSuccess, extraData = {} }) => {
  const [currency, setCurrency] = useState(baseCurrency || 'USD');
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('details');
  const backdropRef = useRef(null);

  // Fetch live rate on open — fetchLiveRate is module-level stable function
  useEffect(() => {
    if (isOpen) fetchLiveRate();
  }, [isOpen]);

  if (!isOpen) return null;

  const amount = convertPrice(baseAmount, baseCurrency, currency);
  const update = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  // Close only when tapping the dark backdrop itself, NOT bubbled from children
  const handleBackdropClick = (e) => {
    if (e.target === backdropRef.current) onClose();
  };

  const buildPayload = () => ({
    amount, currency, item_type: itemType, item_name: itemName,
    customer_email: form.email || '', customer_name: form.name || '',
    message: form.message || '', size: form.size || '',
    address_line1: form.address_line1 || '', address_line2: form.address_line2 || '',
    city: form.city || '', state: form.state || '',
    pincode: form.pincode || '', country: form.country || '',
    phone: form.phone || '', patreon_username: form.patreon_username || '',
    extra_details: form.extra_details || '',
    ...extraData,
  });

  const payWithRazorpay = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/orders/create`, buildPayload());
      const options = {
        key: RAZORPAY_KEY,
        amount: data.amount_paise,
        currency: data.currency,
        name: 'Acid Rani',
        description: itemName,
        order_id: data.razorpay_order_id,
        handler: async (response) => {
          await axios.post(`${API_URL}/api/orders/confirm-razorpay`, {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });
          onSuccess?.(data.order_id);
          onClose();
        },
        prefill: { name: form.name || '', email: form.email || '', contact: form.phone || '' },
        theme: { color: '#FF00FF' },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.warn('Razorpay error:', e);
    }
    setLoading(false);
  };

  const payWithPayPal = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/orders/create`, buildPayload());
      await axios.post(`${API_URL}/api/orders/confirm-paypal`, { order_id: data.order_id });
      const ppUrl = `https://www.paypal.com/cgi-bin/webscr?cmd=_xclick&business=${encodeURIComponent(PAYPAL_EMAIL)}&amount=${amount}&currency_code=${currency}&item_name=${encodeURIComponent(itemName)}&no_shipping=${itemType === 'merch' ? '0' : '1'}`;
      window.open(ppUrl, '_blank');
      onSuccess?.(data.order_id);
      onClose();
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') console.warn('PayPal error:', e);
    }
    setLoading(false);
  };

  const requiredFilled = form.email && (itemType !== 'merch' || (form.address_line1 && form.city && form.pincode && form.country));

  return (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: 'rgba(10,2,8,0.85)', backdropFilter: 'blur(8px)' }}
      onClick={handleBackdropClick}
      data-testid="checkout-backdrop"
    >
      <div
        className="relative w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl p-6 md:p-8"
        style={{ background: 'linear-gradient(135deg, #1A0510 0%, #2D0A1E 100%)', border: '1px solid rgba(255,0,255,0.2)' }}
        data-testid="checkout-modal"
      >
        <button onClick={onClose} className="absolute top-4 right-4 text-white/40 hover:text-white z-10">
          <X size={20} />
        </button>

        <h3 className="font-heading text-xl text-white mb-1">{itemName}</h3>
        <div className="flex items-center gap-3 mb-6">
          <span className="text-[#CCFF00] font-heading text-2xl">{formatPrice(amount, currency)}</span>
          <CurrencyToggle currency={currency} setCurrency={setCurrency} />
        </div>

        {step === 'details' && (
          <div className="space-y-3">
            <Input label="Your Name" value={form.name} onChange={v => update('name', v)} />
            <Input label="Email *" value={form.email} onChange={v => update('email', v)} type="email" />

            {itemType === 'merch' && (
              <>
                <div className="text-white/40 text-xs font-heading uppercase tracking-wider mt-4 mb-2">Shipping Address</div>
                <Input label="Address Line 1 *" value={form.address_line1} onChange={v => update('address_line1', v)} />
                <Input label="Address Line 2" value={form.address_line2} onChange={v => update('address_line2', v)} />
                <div className="grid grid-cols-2 gap-3">
                  <Input label="City *" value={form.city} onChange={v => update('city', v)} />
                  <Input label="State" value={form.state} onChange={v => update('state', v)} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input label="Pincode *" value={form.pincode} onChange={v => update('pincode', v)} />
                  <Input label="Country *" value={form.country} onChange={v => update('country', v)} />
                </div>
                <Input label="Phone" value={form.phone} onChange={v => update('phone', v)} />
              </>
            )}

            {itemType === 'patreon' && (
              <>
                <Input label="Patreon Username" value={form.patreon_username} onChange={v => update('patreon_username', v)} placeholder="@username" />
                <Input label="Any details you want to share" value={form.extra_details} onChange={v => update('extra_details', v)} textarea />
              </>
            )}

            {itemType === 'spoilme' && (
              <div className="mt-2">
                <label className="text-[#FFD1DC] text-xs uppercase tracking-wider mb-2 block font-heading">
                  Write a personal message to Acid Rani
                </label>
                <textarea
                  value={form.message || ''}
                  onChange={e => update('message', e.target.value)}
                  placeholder="Your message... she reads every one personally"
                  className="checkout-textarea"
                  rows={4}
                  data-testid="spoilme-message-input"
                />
              </div>
            )}

            <button
              onClick={() => setStep('payment')}
              disabled={!requiredFilled}
              className={`w-full mt-4 py-3 rounded-full font-heading font-bold text-sm uppercase tracking-wider transition-all ${
                requiredFilled
                  ? 'bg-[#FF00FF] text-white hover:shadow-[0_0_20px_rgba(255,0,255,0.5)]'
                  : 'bg-white/10 text-white/30 cursor-not-allowed'
              }`}
              data-testid="checkout-proceed-btn"
            >
              Proceed to Payment
            </button>
          </div>
        )}

        {step === 'payment' && (
          <div className="space-y-4">
            <p className="text-white/50 text-sm">Choose your payment method</p>
            <button
              onClick={payWithRazorpay} disabled={loading}
              className="w-full py-4 rounded-2xl font-heading font-bold text-sm uppercase tracking-wider flex items-center justify-center gap-3 transition-all hover:scale-[1.02]"
              style={{ background: 'linear-gradient(135deg, #2B83EA 0%, #528FF0 100%)', color: '#fff' }}
              data-testid="pay-razorpay-btn"
            >
              <CreditCard size={18} />
              {loading ? 'Processing...' : 'Pay with Razorpay'}
            </button>
            <button
              onClick={payWithPayPal} disabled={loading}
              className="w-full py-4 rounded-2xl font-heading font-bold text-sm uppercase tracking-wider flex items-center justify-center gap-3 transition-all hover:scale-[1.02]"
              style={{ background: 'linear-gradient(135deg, #FFC439 0%, #FFD700 100%)', color: '#1A0510' }}
              data-testid="pay-paypal-btn"
            >
              <DollarSign size={18} />
              {loading ? 'Processing...' : 'Pay with PayPal'}
            </button>
            <button onClick={() => setStep('details')} className="w-full text-center text-white/40 text-xs hover:text-white/60 mt-2">
              Back to details
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Input component
const Input = ({ label, value, onChange, type = 'text', placeholder, textarea }) => (
  <div>
    <label className="text-white/40 text-xs font-body block mb-1">{label}</label>
    {textarea ? (
      <textarea value={value || ''} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} className="checkout-input resize-none" rows={2} />
    ) : (
      <input type={type} value={value || ''} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} className="checkout-input" />
    )}
  </div>
);

// Order Tracking Modal — same touch fix
export const TrackOrderModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [orderId, setOrderId] = useState('');
  const [orders, setOrders] = useState(null);
  const [loading, setLoading] = useState(false);
  const backdropRef = useRef(null);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === backdropRef.current) onClose();
  };

  const handleTrack = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/orders/track`, { email, order_id: orderId || undefined });
      setOrders(data.orders);
    } catch (e) { setOrders([]); }
    setLoading(false);
  };

  return (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: 'rgba(10,2,8,0.85)', backdropFilter: 'blur(8px)' }}
      onClick={handleBackdropClick}
    >
      <div
        className="relative w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-3xl p-6 md:p-8"
        style={{ background: 'linear-gradient(135deg, #1A0510 0%, #2D0A1E 100%)', border: '1px solid rgba(255,0,255,0.2)' }}
        data-testid="track-order-modal"
      >
        <button onClick={onClose} className="absolute top-4 right-4 text-white/40 hover:text-white"><X size={20} /></button>
        <h3 className="font-heading text-xl text-white mb-4">Track Your Order</h3>
        <div className="space-y-3 mb-4">
          <Input label="Email *" value={email} onChange={setEmail} type="email" />
          <Input label="Order ID (optional)" value={orderId} onChange={setOrderId} placeholder="ORD-XXXXXXXX" />
        </div>
        <button
          onClick={handleTrack} disabled={!email || loading}
          className="w-full py-3 rounded-full bg-[#FF00FF] text-white font-heading font-bold text-sm uppercase tracking-wider disabled:opacity-30"
          data-testid="track-submit-btn"
        >
          {loading ? 'Searching...' : 'Find My Orders'}
        </button>
        {orders !== null && (
          <div className="mt-6 space-y-3">
            {orders.length === 0 ? (
              <p className="text-white/40 text-sm text-center">No orders found for this email.</p>
            ) : orders.map(o => (
              <div key={o.order_id} className="p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="flex justify-between items-start mb-2">
                  <span className="text-white font-heading text-sm">{o.item_name}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${o.payment_status === 'paid' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                    {o.payment_status === 'paid' ? 'PAID' : 'PENDING'}
                  </span>
                </div>
                <p className="text-white/40 text-xs">{o.order_id} · {o.currency} {o.amount}</p>
                <p className="text-white/30 text-xs mt-1">{new Date(o.created_at).toLocaleDateString()}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
